import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import {
  companyImpact,
  createCompany,
  deleteCompany,
  listCompanies,
  updateCompany,
  type Company,
  type CompanyImpact,
} from '../api/directory';
import { ClickableRow, RowTitle } from '../components/ClickableRow';
import { CompanyReportPage } from './CompanyReportPage';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toasts';
import { Icon } from '../lib/icons';

const PAGE_SIZE = 50;

/**
 * FEAT-011 — the Companies registry.
 *
 * Same shape as the Support screen: a card with a tool row, a scoped table, and
 * dialogs built from the shared `Modal`.
 */
export function CompaniesPage(): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Company | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleting, setDeleting] = useState<Company | null>(null);
  /**
   * FEAT-021 — selecting a company replaces the list with its report, exactly as
   * the prototype does. Held as state rather than a route because the report is
   * a view of the registry, and "Back to companies" should return to the list
   * with its search and page intact.
   */
  const [reportFor, setReportFor] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['companies', search],
    queryFn: () =>
      listCompanies({ page: 1, pageSize: PAGE_SIZE, ...(search ? { q: search } : {}) }),
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
  }

  if (reportFor) {
    return (
      <CompanyReportPage
        companyId={reportFor}
        onBack={() => setReportFor(null)}
        onEditBasics={() => {
          const company = query.data?.items.find((c) => c.id === reportFor);
          if (company) setEditing(company);
        }}
      />
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h1>{t('companies')}</h1>

          <div className="ch-tools">
            <label className="sr-only" htmlFor="company-search">
              {t('search_companies')}
            </label>
            <input
              id="company-search"
              type="search"
              placeholder={t('search_companies')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <button type="button" className="btn primary" onClick={() => setIsCreating(true)}>
              <Icon name="plus" />
              {t('new_company')}
            </button>
          </div>
        </div>

        {query.isPending ? (
          <div className="empty" role="status" aria-live="polite">
            {t('loading')}
          </div>
        ) : query.error ? (
          <div className="empty" role="alert">
            {query.error instanceof ApiClientError ? query.error.message : t('login_network_error')}
          </div>
        ) : query.data && query.data.items.length === 0 ? (
          <div className="empty">{t('no_companies')}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('col_company')}</th>
                  <th scope="col">{t('col_cr')}</th>
                  <th scope="col">{t('col_website')}</th>
                  <th scope="col">{t('col_people')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('col_actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data?.items.map((company) => (
                  <ClickableRow key={company.id} onOpen={() => setReportFor(company.id)}>
                    <td>
                      <RowTitle onOpen={() => setReportFor(company.id)}>{company.name}</RowTitle>
                      {/* The house company is the one everyone looks for. */}
                      {company.is_house ? <span className="chip">{t('house_company')}</span> : null}
                    </td>
                    <td>{company.cr_number}</td>
                    <td>{company.website ?? '—'}</td>
                    <td>{company.employee_count}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => setEditing(company)}
                          aria-label={`${t('edit')} ${company.name}`}
                        >
                          <Icon name="edit" />
                        </button>

                        {/*
                        The house company cannot be deleted (BR-030), so the
                        control is not offered. The server refuses it regardless.
                      */}
                        {company.is_house ? null : (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => setDeleting(company)}
                            aria-label={`${t('delete')} ${company.name}`}
                          >
                            <Icon name="trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isCreating || editing ? (
        <CompanyDialog
          company={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
          onSaved={(name) => {
            setIsCreating(false);
            setEditing(null);
            refresh();
            notify(t('company_saved', { name }));
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteCompanyDialog
          company={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(name) => {
            setDeleting(null);
            refresh();
            notify(t('company_deleted', { name }));
          }}
        />
      ) : null}
    </>
  );
}

function CompanyDialog({
  company,
  onClose,
  onSaved,
}: {
  company: Company | null;
  onClose: () => void;
  onSaved: (name: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const [name, setName] = useState(company?.name ?? '');
  const [crNumber, setCrNumber] = useState(company?.cr_number ?? '');
  const [website, setWebsite] = useState(company?.website ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () => {
      const body = { name, cr_number: crNumber, website };
      return company ? updateCompany(company.id, body) : createCompany(body);
    },
    onSuccess: (saved) => onSaved(saved.name),
    onError: (error) => {
      if (error instanceof ApiClientError) {
        // Every offending field at once (§8.6.3), and a duplicate name is
        // attributed to the field rather than shown as a bare banner.
        setFieldErrors(error.fieldErrors);
        if (error.code === 'duplicate_name') {
          setFieldErrors({ name: error.message });
        }
        notify(error.message, 'bad');
      }
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    mutation.mutate();
  }

  return (
    <Modal
      title={company ? t('edit_company') : t('new_company')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="company-form"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('save')}
          </button>
        </>
      }
    >
      <form id="company-form" onSubmit={submit} noValidate>
        <Field
          id="company-name"
          label={t('col_company')}
          value={name}
          onChange={setName}
          error={fieldErrors.name}
          required
        />
        <Field
          id="company-cr"
          label={t('col_cr')}
          value={crNumber}
          onChange={setCrNumber}
          error={fieldErrors.cr_number}
          required
        />
        <Field
          id="company-website"
          label={t('col_website')}
          value={website}
          onChange={setWebsite}
          error={fieldErrors.website}
          optional
        />
      </form>
    </Modal>
  );
}

/**
 * Confirms a delete by showing what it would take with it (REQ-066).
 *
 * The impact is fetched rather than guessed, because "delete this company" and
 * "delete this company, its 14 people and their 62 tickets" are different
 * decisions and the administrator should be making the second one.
 */
function DeleteCompanyDialog({
  company,
  onClose,
  onDeleted,
}: {
  company: Company;
  onClose: () => void;
  onDeleted: (name: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const impact = useQuery<CompanyImpact>({
    queryKey: ['company-impact', company.id],
    queryFn: () => companyImpact(company.id),
  });

  const mutation = useMutation({
    mutationFn: () => deleteCompany(company.id),
    onSuccess: () => onDeleted(company.name),
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={t('delete_company')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || impact.data?.can_delete === false}
          >
            {mutation.isPending ? t('saving') : t('delete')}
          </button>
        </>
      }
    >
      {impact.isPending ? (
        <div className="empty" role="status" aria-live="polite">
          {t('loading')}
        </div>
      ) : impact.data ? (
        <>
          <p>{t('delete_company_confirm', { name: company.name })}</p>

          <ul className="tl">
            <li>{t('impact_employees', { count: impact.data.employees })}</li>
            <li>{t('impact_tickets', { count: impact.data.tickets })}</li>
          </ul>

          {impact.data.can_delete ? null : (
            <div className="login-error" role="alert">
              <Icon name="info" />
              <span>{t('house_company_undeletable')}</span>
            </div>
          )}
        </>
      ) : null}
    </Modal>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  required,
  optional,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  /** Marked with the prototype's `.opt` span. */
  optional?: boolean;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optional ? <span className="opt"> {t('optional')}</span> : null}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={error ? 'bad' : ''}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        required={required}
      />
      {/* Tied to the input by aria-describedby: colour alone is not a message. */}
      {error ? (
        <div id={`${id}-error`} className="field-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
